/**
 * REST API クライアント
 */
import { fetchAuthSession } from "@aws-amplify/auth";

// REST API エンドポイント
const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || "";
const REST_API_BASE_URL = `${API_ENDPOINT}/api`;

/**
 * 認証トークンを取得（タイムアウト付き）
 */
async function getAuthToken(): Promise<string | null> {
  try {
    const timeout = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error("Auth session timeout")), 5000),
    );
    const session = await Promise.race([fetchAuthSession(), timeout]);
    // Cognito User Pool Authorizer requires ID Token (not Access Token)
    return session.tokens?.idToken?.toString() || null;
  } catch (_e) {
    return null;
  }
}

/**
 * REST API リクエストを送信
 * @param {string} path - API パス
 * @param {RequestInit} options - fetch オプション
 * @param {boolean} skipAuth - 認証をスキップするか
 * @return {Promise<T>} API レスポンス
 */
async function request<T>(
  path: string,
  options: RequestInit = {},
  skipAuth = false,
): Promise<T> {
  const token = skipAuth ? null : await getAuthToken();

  const headers: HeadersInit = {
    "Content-Type": "application/json",
    // biome-ignore lint/style/useNamingConvention: HTTP header
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const response = await fetch(`${REST_API_BASE_URL}${path}`, {
    ...options,
    headers,
    mode: "cors",
    credentials: "omit",
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new RestApiError(
      errorBody.message || errorBody.detail || `HTTP error ${response.status}`,
      response.status,
      errorBody,
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

/**
 * 任意のレスポンス処理を行う生 fetch (Blob / FormData 系で使用)。
 * @param {string} path - API パス
 * @param {RequestInit} options - fetch オプション (Content-Type は呼び出し側で制御)
 * @return {Promise<Response>} 生のレスポンス
 */
async function rawRequest(
  path: string,
  options: RequestInit = {},
): Promise<Response> {
  const token = await getAuthToken();
  const headers: HeadersInit = {
    // biome-ignore lint/style/useNamingConvention: HTTP header
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };
  const response = await fetch(`${REST_API_BASE_URL}${path}`, {
    ...options,
    headers,
    mode: "cors",
    credentials: "omit",
  });
  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new RestApiError(
      errorBody.message || errorBody.detail || `HTTP error ${response.status}`,
      response.status,
      errorBody,
    );
  }
  return response;
}

/**
 * REST API エラークラス
 */
export class RestApiError extends Error {
  /**
   * @param {string} message - エラーメッセージ
   * @param {number} status - HTTP ステータスコード
   * @param {unknown} body - レスポンスボディ
   */
  constructor(
    message: string,
    public status: number,
    public body?: unknown,
  ) {
    super(message);
    this.name = "RestApiError";
  }
}

/**
 * REST API クライアント
 * @description GET/POST/PUT/DELETE メソッドを提供
 */
export const restClient = {
  get: <T>(path: string, params?: Record<string, string>) =>
    request<T>(
      params ? `${path}?${new URLSearchParams(params).toString()}` : path,
    ),

  post: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: "POST",
      body: body ? JSON.stringify(body) : undefined,
    }),

  put: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: "PUT",
      body: body ? JSON.stringify(body) : undefined,
    }),

  delete: <T>(path: string) =>
    request<T>(path, {
      method: "DELETE",
    }),

  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: "PATCH",
      body: body ? JSON.stringify(body) : undefined,
    }),

  /**
   * 認証不要のGETリクエスト
   * @param {string} path - APIパス
   * @param {Record<string, string>} params - クエリパラメータ
   * @return {Promise<T>} レスポンス
   */
  publicGet: <T>(path: string, params?: Record<string, string>) =>
    request<T>(
      params ? `${path}?${new URLSearchParams(params).toString()}` : path,
      {},
      true,
    ),

  /**
   * FormData (multipart/form-data) を POST する。
   * Content-Type は fetch が boundary 付きで自動設定するため明示しない。
   * @param {string} path - APIパス
   * @param {FormData} formData - 送信する FormData
   * @return {Promise<T>} レスポンス
   */
  postFormData: async <T>(path: string, formData: FormData): Promise<T> => {
    const response = await rawRequest(path, {
      method: "POST",
      body: formData,
    });
    if (response.status === 204) return undefined as T;
    return response.json();
  },

  /**
   * Blob レスポンスを取得する (CSV / ファイルダウンロード用)。
   * @param {string} path - APIパス
   * @param {Record<string, string>} params - クエリパラメータ
   * @return {Promise<object>} blob (Blob) と filename (string|null) を持つオブジェクト
   */
  getBlob: async (
    path: string,
    params?: Record<string, string>,
  ): Promise<{ blob: Blob; filename: string | null }> => {
    const queryPath = params
      ? `${path}?${new URLSearchParams(params).toString()}`
      : path;
    const response = await rawRequest(queryPath);
    const blob = await response.blob();
    const contentDisposition =
      response.headers.get("content-disposition") || "";
    const match = contentDisposition.match(/filename="?([^";]+)"?/);
    const filename = match ? match[1] : null;
    return { blob, filename };
  },
};

export default restClient;
